import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { apiService } from "../services/api.service";
import { Card } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { X, Zap, Activity, RefreshCw, Radio, Gauge } from "lucide-react";
import { useHeaderActions } from "../context/HeaderActionsContext";

import {
  TestHistoryPanel,
  LoadTestConfigForm,
  TestStatusBanner,
  TestSummaryCards,
  ClientResultsGrid,
  AudioPlayer,
  RunNotesPanel,
  ServiceLatencyPanel,
  LoadingState,
  EmptyState,
  getDefaultConfig,
  type LoadTestConfig,
  type LoadTestStatus,
  type TestListItem,
  type GetClientsResponse,
  type ClientWithAudio,
  type ActiveTab,
} from "./load-test";

const POLL_INTERVAL_MS = 5000;

export const LoadTestDashboard = () => {
  const { setActions } = useHeaderActions();

  // Form state
  const [config, setConfig] = useState<LoadTestConfig>(() =>
    getDefaultConfig(),
  );
  // Notes the user must write before starting a run. Saved to the run once it
  // has a test id (notes are keyed by test id on the backend).
  const [notes, setNotes] = useState("");

  // Running test — the live test for this session. Persists independently of
  // which historical test the user is browsing, so the "Running" tab always
  // reflects the active run and the user can return to it at any time.
  const [runningTestId, setRunningTestId] = useState<string | null>(null);
  const [runningStatus, setRunningStatus] = useState<LoadTestStatus | null>(
    null,
  );
  const [runningResults, setRunningResults] =
    useState<GetClientsResponse | null>(null);

  // Viewed test — a historical test opened from the panel, shown on the
  // "Results" tab. Browsing this never disturbs the running test above.
  const [viewedTestId, setViewedTestId] = useState<string | null>(null);
  const [viewedStatus, setViewedStatus] = useState<LoadTestStatus | null>(null);
  const [viewedResults, setViewedResults] =
    useState<GetClientsResponse | null>(null);
  const [isLoadingViewed, setIsLoadingViewed] = useState(false);

  // UI state
  const [isStarting, setIsStarting] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>("config");

  // History state (from API)
  const [testHistory, setTestHistory] = useState<TestListItem[]>([]);

  // Audio playback state
  const [playingClientId, setPlayingClientId] = useState<number | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioTestId, setAudioTestId] = useState<string | null>(null);

  // Polling ref — one poller for the running test only.
  const pollingIntervalRef = useRef<number | null>(null);

  const isRunning = runningStatus?.status === "running";

  // Load test history from API
  const loadTestHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const response = await apiService.listLoadTests(20);
      // Always order by when the run started. Older runs predate meta.json and
      // have no started_at — fall back to last_modified so they still sort.
      const sorted = [...response.tests].sort((a, b) => {
        const aTime = Date.parse(a.started_at ?? a.last_modified ?? "");
        const bTime = Date.parse(b.started_at ?? b.last_modified ?? "");
        return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
      });
      setTestHistory(sorted);
    } catch (e) {
      console.error("Failed to load test history:", e);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadTestHistory();
  }, [loadTestHistory]);

  const stopRunningPoller = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Poll the running test's status; on completion, fetch its results and stop.
  const pollRunningStatus = useCallback(
    async (id: string) => {
      try {
        const statusResponse = await apiService.getLoadTestStatus(id);
        setRunningStatus(statusResponse);

        if (statusResponse.status === "completed") {
          stopRunningPoller();
          try {
            const clientsResponse = await apiService.getLoadTestClients(id);
            setRunningResults(clientsResponse);
          } catch (e) {
            console.error("Failed to fetch running results:", e);
          }
          loadTestHistory();
        }
      } catch (e) {
        console.error("Failed to poll status:", e);
        setError("Failed to get test status");
      }
    },
    [loadTestHistory, stopRunningPoller],
  );

  // Adopt a test as THE running test: reset its slot and start the poller.
  const startRunningPoll = useCallback(
    (id: string) => {
      stopRunningPoller();
      setRunningTestId(id);
      setRunningStatus(null);
      setRunningResults(null);
      pollingIntervalRef.current = window.setInterval(() => {
        pollRunningStatus(id);
      }, POLL_INTERVAL_MS);
      pollRunningStatus(id);
    },
    [pollRunningStatus, stopRunningPoller],
  );

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopRunningPoller();
  }, [stopRunningPoller]);

  // Start load test
  const handleStartTest = async () => {
    const trimmedNotes = notes.trim();
    if (!trimmedNotes) {
      setError("Add a note describing this run before starting it.");
      return;
    }

    setError(null);
    setIsStarting(true);

    try {
      const response = await apiService.startLoadTest(config);
      // Persist the pre-run notes against the new run, then clear the form so
      // the next run starts from a blank note.
      try {
        await apiService.saveLoadTestNotes(response.test_id, trimmedNotes);
        setNotes("");
      } catch (e) {
        console.error("Failed to save run notes:", e);
        toast.error("Test started, but saving the notes failed");
      }
      startRunningPoll(response.test_id);
      setActiveTab("running");
      loadTestHistory();
    } catch (e) {
      console.error("Failed to start load test:", e);
      setError(e instanceof Error ? e.message : "Failed to start load test");
    } finally {
      setIsStarting(false);
    }
  };

  // View a past test. Clicking the active run just returns to its tab — it must
  // not start a second poller (the bug in the old single-state design). Any
  // other test loads into the viewed slot and shows on the Results tab.
  const handleViewTest = async (test: TestListItem) => {
    if (test.test_id === runningTestId) {
      setActiveTab("running");
      return;
    }

    setError(null);
    setViewedTestId(test.test_id);
    setIsLoadingViewed(true);
    setViewedResults(null);
    setViewedStatus(null);
    setActiveTab("results");

    try {
      const [statusResponse, clientsResponse] = await Promise.all([
        apiService.getLoadTestStatus(test.test_id),
        apiService.getLoadTestClients(test.test_id),
      ]);

      // The test turned out to be running and we have no active run tracked —
      // promote it to the running slot so it gets a live tab + poller.
      if (statusResponse.status === "running" && !runningTestId) {
        startRunningPoll(test.test_id);
        setViewedTestId(null);
        setActiveTab("running");
        return;
      }

      setViewedStatus(statusResponse);
      setViewedResults(clientsResponse);
    } catch (e) {
      console.error("Failed to fetch test:", e);
      setError("Failed to load test results");
    } finally {
      setIsLoadingViewed(false);
    }
  };

  // Delete test
  const handleDeleteTest = async (
    deletingTestId: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    if (!confirm("Delete this test and all its data?")) return;

    try {
      await apiService.deleteLoadTest(deletingTestId);
      loadTestHistory();
      if (deletingTestId === runningTestId) {
        stopRunningPoller();
        setRunningTestId(null);
        setRunningStatus(null);
        setRunningResults(null);
      }
      if (deletingTestId === viewedTestId) {
        setViewedResults(null);
        setViewedStatus(null);
        setViewedTestId(null);
      }
    } catch (e) {
      console.error("Failed to delete test:", e);
      setError("Failed to delete test");
    }
  };

  // Play audio for client
  const handlePlayAudio = (client: ClientWithAudio, sourceTestId: string | null) => {
    if (client.audio_url) {
      setAudioUrl(client.audio_url);
      setPlayingClientId(client.client_id);
      setAudioTestId(sourceTestId);
    }
  };

  // Close audio player
  const handleClosePlayer = () => {
    setAudioUrl(null);
    setPlayingClientId(null);
    setAudioTestId(null);
  };

  // Handle input changes
  const handleInputChange = (
    field: keyof LoadTestConfig,
    value: string | number,
  ) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  // Header summary
  const totalTests = testHistory.length;
  const activeStatusLabel = isRunning
    ? "Running"
    : runningStatus?.status === "completed"
      ? "Completed"
      : "Idle";

  // Set header actions
  useEffect(() => {
    setActions(
      <div className="flex items-center gap-2">
        <Badge
          className={`rounded-full border px-3 py-1 text-xs ${isRunning ? "border-lime-600/30 bg-lime-600/10 text-lime-900 animate-pulse" : "border-border bg-muted text-muted-foreground"}`}
        >
          {activeStatusLabel} • {totalTests} tests
        </Badge>
        <Button
          variant="outline"
          size="icon"
          onClick={loadTestHistory}
          disabled={isLoadingHistory}
          title="Refresh history"
          className="h-8 w-8"
        >
          <RefreshCw
            className={`h-4 w-4 ${isLoadingHistory ? "animate-spin" : ""}`}
          />
        </Button>
      </div>,
    );
    return () => setActions(null);
  }, [
    setActions,
    isRunning,
    activeStatusLabel,
    totalTests,
    isLoadingHistory,
    loadTestHistory,
  ]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto">
      <div className="flex-1 min-h-[420px] grid grid-cols-[288px_minmax(0,1fr)] gap-4 max-md:grid-cols-1 max-md:min-h-fit">
        {/* Test History Panel */}
        <TestHistoryPanel
          testHistory={testHistory}
          selectedTestId={activeTab === "running" ? runningTestId : viewedTestId}
          runningTestId={runningTestId}
          isLoadingHistory={isLoadingHistory}
          onRefresh={loadTestHistory}
          onViewTest={handleViewTest}
          onDeleteTest={handleDeleteTest}
        />

        {/* Main Content */}
        <Card className="flex min-h-0 flex-col overflow-hidden border border-border/60 bg-card/80 shadow-sm">
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as ActiveTab)}
            className="flex flex-col h-full"
          >
            <div className="flex items-center gap-1 border-b border-border/60 px-4 py-2">
              <TabsList
                variant="line"
                className="h-auto gap-1 bg-transparent p-0"
              >
                <TabsTrigger
                  value="config"
                  className="gap-2 rounded-lg border-0 px-3 py-1.5 text-sm font-medium text-muted-foreground transition-all after:hidden hover:text-foreground data-[state=active]:bg-lime-600/10 data-[state=active]:text-lime-900 data-[state=active]:ring-1 data-[state=active]:ring-lime-600/20"
                >
                  <Zap className="size-3.5" />
                  Configuration
                </TabsTrigger>
                <TabsTrigger
                  value="running"
                  disabled={!runningTestId}
                  className="gap-2 rounded-lg border-0 px-3 py-1.5 text-sm font-medium text-muted-foreground transition-all after:hidden hover:text-foreground data-[state=active]:bg-lime-600/10 data-[state=active]:text-lime-900 data-[state=active]:ring-1 data-[state=active]:ring-lime-600/20 disabled:opacity-40"
                >
                  <Radio className="size-3.5" />
                  Running Test
                  {isRunning && (
                    <Badge className="h-4 rounded-full border border-lime-600/30 bg-lime-600/15 px-1.5 text-[0.6rem] font-semibold text-lime-800 animate-pulse">
                      LIVE
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="results"
                  disabled={!viewedResults && !isLoadingViewed}
                  className="gap-2 rounded-lg border-0 px-3 py-1.5 text-sm font-medium text-muted-foreground transition-all after:hidden hover:text-foreground data-[state=active]:bg-lime-600/10 data-[state=active]:text-lime-900 data-[state=active]:ring-1 data-[state=active]:ring-lime-600/20 disabled:opacity-40"
                >
                  <Activity className="size-3.5" />
                  Results
                </TabsTrigger>
                <TabsTrigger
                  value="latency"
                  className="gap-2 rounded-lg border-0 px-3 py-1.5 text-sm font-medium text-muted-foreground transition-all after:hidden hover:text-foreground data-[state=active]:bg-lime-600/10 data-[state=active]:text-lime-900 data-[state=active]:ring-1 data-[state=active]:ring-lime-600/20"
                >
                  <Gauge className="size-3.5" />
                  Service Latency
                </TabsTrigger>
              </TabsList>
            </div>

            {error && (
              <div className="flex justify-between items-center px-4 py-3 bg-destructive/10 border-b text-destructive text-sm">
                <span className="font-medium">{error}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 hover:bg-muted"
                  onClick={() => setError(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Config Tab */}
            <TabsContent
              value="config"
              className="flex-1 p-4 overflow-y-auto flex justify-center items-start m-0"
            >
              <LoadTestConfigForm
                config={config}
                isRunning={isRunning}
                isLoading={isStarting}
                notes={notes}
                onConfigChange={handleInputChange}
                onNotesChange={setNotes}
                onStartTest={handleStartTest}
              />
            </TabsContent>

            {/* Running Test Tab */}
            <TabsContent
              value="running"
              className="flex-1 p-4 overflow-y-auto m-0"
            >
              <TestStatusBanner
                status={runningStatus}
                testId={runningTestId}
                isRunning={isRunning}
                totalClients={runningStatus?.completed_clients ?? 0}
                runningTasks={runningStatus?.running_tasks ?? 0}
              />

              {runningResults?.summary && (
                <TestSummaryCards summary={runningResults.summary} />
              )}

              {runningResults?.clients && runningResults.clients.length > 0 && (
                <ClientResultsGrid
                  clients={runningResults.clients}
                  onPlayAudio={(client) =>
                    handlePlayAudio(client, runningTestId)
                  }
                />
              )}

              {isRunning && !runningResults && <LoadingState />}
            </TabsContent>

            {/* Results Tab (historical / viewed test) */}
            <TabsContent
              value="results"
              className="flex-1 p-4 overflow-y-auto m-0"
            >
              <TestStatusBanner
                status={viewedStatus}
                testId={viewedTestId}
                isRunning={false}
                totalClients={viewedStatus?.completed_clients ?? 0}
                runningTasks={viewedStatus?.running_tasks ?? 0}
              />

              {viewedResults?.summary && (
                <TestSummaryCards summary={viewedResults.summary} />
              )}

              {viewedTestId && viewedResults && (
                <RunNotesPanel key={viewedTestId} testId={viewedTestId} />
              )}

              {viewedResults?.clients && viewedResults.clients.length > 0 && (
                <ClientResultsGrid
                  clients={viewedResults.clients}
                  onPlayAudio={(client) => handlePlayAudio(client, viewedTestId)}
                />
              )}

              {isLoadingViewed && !viewedResults && <LoadingState />}

              {!viewedResults && !isLoadingViewed && <EmptyState />}
            </TabsContent>

            {/* Service Latency Tab (per-dependency probe) */}
            <TabsContent
              value="latency"
              className="flex-1 p-4 overflow-y-auto m-0"
            >
              <ServiceLatencyPanel />
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      {/* Audio Player */}
      {audioUrl && (
        <AudioPlayer
          audioUrl={audioUrl}
          playingClientId={playingClientId}
          testId={audioTestId}
          onClose={handleClosePlayer}
        />
      )}
    </div>
  );
};
